<div [ngClass]="{ 'error': error }" class="datepicker-container">
  @if (label) {
    <label class="datepicker-label" [for]="inputId">{{ label }}</label>
  }
  
  @if(view === 'week' && inline) {
    <div class="week-view">
      <div class="week-header">
        <div class="week-navigation">
          <button type="button" class="nav-button" (click)="previousWeek()">
            <lucide-icon name="ChevronLeft"></lucide-icon>
          </button>
          @for(day of getWeekDays(); track day.date) {
            <div class="day-number" 
              [class.active]="isSelectedDay(day.date)"
              [class.today]="isToday(day.date)"
              (click)="selectDay(day.date)">
              {{ day.number }}
              @if(hasEvents(day.date)) {
                <span class="event-dot"></span>
              }
              @if(hasRequests(day.date)) {
                <span class="request-dot"></span>
              }
            </div>
          }
          <button type="button" class="nav-button" (click)="nextWeek()">
            <lucide-icon name="ChevronRight"></lucide-icon>
          </button>
        </div>
      </div>
    </div>
  } @else if(inline) {
    <p-datepicker
      [(ngModel)]="value"
      [inline]="true"
      [showWeek]="false"
      [disabled]="disabled"
      [ngClass]="[size]"
      class="custom-datepicker"
      [selectionMode]="rangeMode ? 'range' : 'single'"
      [defaultDate]="defaultDate"
      [minDate]="minDate"
      [maxDate]="maxDate"
      dateFormat="dd-mm-yy"
    >
      <ng-template pTemplate="date" let-date>
        <span [class.has-event]="hasEvents(date)" [class.has-request]="hasRequests(date)">{{date.day}}</span>
      </ng-template>
    </p-datepicker>
  } @else {
    @if(useSpecialContainer) {
      <!-- Inline calendar design for all absence types -->
      <div class="date-time-container">
        <div class="date-input-section">
          <input 
            type="text" 
            class="date-input"
            [placeholder]="placeholder"
            [value]="getFormattedDateRange()"
            readonly
            (click)="toggleCalendar()"
          >
          <lucide-icon name="Calendar" class="calendar-icon" (click)="toggleCalendar()"></lucide-icon>
        </div>
        
        @if(showCalendar) {
          <div class="inline-calendar-container">
            <p-datepicker
              [ngModel]="value"
              (ngModelChange)="onDateSelect($event)"
              [inline]="true"
              [showWeek]="false"
              [disabled]="disabled"
              class="custom-inline-datepicker"
              [selectionMode]="showTimeInputs ? 'single' : (rangeMode ? 'range' : 'single')"
              [defaultDate]="defaultDate"
              [minDate]="minDate"
              [maxDate]="maxDate"
              dateFormat="dd-mm-yy"
            >
              <ng-template pTemplate="date" let-date>
                <span [class.has-event]="hasEvents(date)">{{date.day}}</span>
              </ng-template>
            </p-datepicker>
          </div>
        }
        
        <!-- Time inputs only visible when showTimeInputs is true -->
        @if(showTimeInputs) {
          <div class="time-inputs-section">
            <div class="time-row">
              <div class="time-field">
                <label class="time-label">{{ 'datepicker.time.start.label' | transloco }}</label>
                <div class="time-input-container">
                  <input 
                    type="text" 
                    class="time-input"
                    [value]="startTime"
                    readonly
                    (click)="openTimeSelector('start')"
                    [placeholder]="'datepicker.time.start.placeholder' | transloco"
                  >
                  <lucide-icon name="Calendar" class="time-icon"></lucide-icon>
                </div>
              </div>
              
              <div class="time-field">
                <label class="time-label">{{ 'datepicker.time.end.label' | transloco }}</label>
                <div class="time-input-container">
                  <input 
                    type="text" 
                    class="time-input"
                    [value]="endTime"
                    readonly
                    (click)="openTimeSelector('end')"
                    [placeholder]="'datepicker.time.end.placeholder' | transloco"
                  >
                  <lucide-icon name="Calendar" class="time-icon"></lucide-icon>
                </div>
              </div>
            </div>
            
            <!-- Inline Time Selector -->
            @if(showTimeSelector) {
              <div class="inline-time-selector">
                <p class="time-selector-title">{{ 'datepicker.time.selector.title' | transloco }}</p>
                
                <div class="time-spinner-container">
                  <div class="time-spinner">
                    <button type="button" class="spinner-btn" (click)="incrementHour()">
                      <lucide-icon name="ChevronUp"></lucide-icon>
                    </button>
                    <div class="spinner-value">{{ formatNumber(tempHour) }}</div>
                    <button type="button" class="spinner-btn" (click)="decrementHour()">
                      <lucide-icon name="ChevronDown"></lucide-icon>
                    </button>
                  </div>
                  
                  <div class="time-separator">:</div>
                  
                  <div class="time-spinner">
                    <button type="button" class="spinner-btn" (click)="incrementMinute()">
                      <lucide-icon name="ChevronUp"></lucide-icon>
                    </button>
                    <div class="spinner-value">{{ formatNumber(tempMinute) }}</div>
                    <button type="button" class="spinner-btn" (click)="decrementMinute()">
                      <lucide-icon name="ChevronDown"></lucide-icon>
                    </button>
                  </div>
                </div>
                
                <div class="time-selector-actions">
                  <button type="button" class="confirm-btn" (click)="confirmTimeSelection()">{{ 'datepicker.time.selector.confirm' | transloco }}</button>
                </div>
              </div>
            }
          </div>
        }
      </div>
    } @else {
      <!-- Regular popup datepicker -->
      <p-datepicker
        #datepickerRef
        [(ngModel)]="value"
        [iconDisplay]="'input'"
        [showIcon]="true"
        [fluid]="true"
        [inputId]="inputId"
        [readonlyInput]="readonlyInput"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [ngClass]="[size]"
        class="custom-datepicker"
        [selectionMode]="rangeMode ? 'range' : 'single'"
        (onSelect)="onDateSelect($event)"
        (onShow)="onDatepickerShow()"
        [appendTo]="appendTo"
        [panelStyleClass]="panelStyleClass"
        [defaultDate]="defaultDate"
        [minDate]="minDate"
        [maxDate]="maxDate"
        dateFormat="dd-mm-yy"
      >
        <!-- Lucide icon (consistent with the rest of the app; PrimeNG v21 rendered its CalendarIcon) -->
        <ng-template pTemplate="inputicon">
          <lucide-icon name="Calendar" class="p-datepicker-input-icon"></lucide-icon>
        </ng-template>
        <ng-template pTemplate="date" let-date>
          <span [class.has-event]="hasEvents(date)">{{date.day}}</span>
        </ng-template>
      </p-datepicker>
    }
  }

  @if(error){
    <div class="error-message">{{ errorMessage }}</div>
  }
</div>